import useQuestionnaire from 'hooks/useQuestionnaire';
import { checkSpanishLocale, cn, extractLabelAndComment } from 'lib/utils';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import { useGetQuestionnaireOptionSearchV3 } from 'services/profile/hooks';
import { useEffect, useMemo, useState } from 'react';
import { SearchChoice } from 'services/profile/type';
import {
  ENGLISH_NONE_OF_THE_ABOVE,
  ENGLISH_OTHER,
  QUESTIONNAIRE_CODE,
} from 'lib/constants';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import MedicalConditionSearch from 'pages/profile/questionnaire/components/MedicalConditionSearch';

export default function FamilyMedicalCondition() {
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const [selectedConditions, setSelectedConditions] = useState<SearchChoice[]>(
    []
  );
  const [openSearch, setOpenSearch] = useState(false);
  const [searchValues, setSearchValues] = useState<SearchChoice[]>([]);

  const { data: searchOptions } = useGetQuestionnaireOptionSearchV3({
    code: questionData?.code || QUESTIONNAIRE_CODE.FAMILY_MEDICAL_CONDITIONS,
    search_text: '',
  });

  const englishChoices = JSON.parse(questionData?.eng_choices || '{}');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '{}');

  const familyMedicalConditions: { en: string; es: string }[] = useMemo(
    () =>
      englishChoices
        .map((choice: string, index: number) => ({
          en: typeof choice === 'string' ? choice : Object.keys(choice)[0],
          es:
            typeof choice === 'string'
              ? spanishChoices[index]
              : Object.keys(spanishChoices[index])[0],
        }))
        .filter(
          (choice: { en: string }) =>
            choice.en !== ENGLISH_OTHER &&
            choice.en !== ENGLISH_NONE_OF_THE_ABOVE
        ),
    [englishChoices, spanishChoices]
  );

  const mostCommonConditions: { en: SearchChoice; es: SearchChoice }[] =
    useMemo(
      () =>
        familyMedicalConditions.map((choice) => ({
          en: { id: null, name: choice.en },
          es: { id: null, name: choice.es },
        })),
      [familyMedicalConditions]
    );
  const localeCommonConditions = useMemo(
    () =>
      mostCommonConditions.map(
        (condition) => condition?.[isSpanish ? 'es' : 'en']
      ),
    [mostCommonConditions, isSpanish]
  );

  useEffect(() => {
    setSelectedConditions(searchValues);
  }, [searchValues]);

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const answer =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];

      let parsedAnswer;
      try {
        parsedAnswer = JSON.parse(answer);
      } catch (error) {
        console.log(error);
        parsedAnswer = answer;
      }

      if (Array.isArray(parsedAnswer) && parsedAnswer?.length) {
        let selected: SearchChoice[] = [];
        if (parsedAnswer[0]?.choice) {
          selected = parsedAnswer.map(
            (item: { id: string; choice: string }) => ({
              id: item.id,
              name: item.choice,
            })
          );
        } else {
          selected = parsedAnswer
            .map((item: unknown) => {
              if (typeof item === 'string') {
                return { id: null, name: item };
              }

              if (typeof item === 'object') {
                return {
                  id: null,
                  name: Object.keys(item as Record<string, string[]>)[0],
                };
              }

              return { id: null, name: '' };
            })
            .filter((item) => item && item?.name);
        }
        const filteredSelect = selected.filter(
          (select) => select.name !== 'None'
        );
        setSelectedConditions(filteredSelect);
        return;
      }

      const answerArray = answer
        .split(',')
        .map((item) => item.trim().replace(/[[\]']/g, ''));
      const answerWithId = answerArray.map((ans) => ({
        id: null,
        name: ans,
      }));
      setSelectedConditions(answerWithId);
    }
  }, [isSpanish, questionData]);

  const onHandleSelection = (condition: SearchChoice) => {
    if (selectedConditions.map((c) => c.name).includes(condition.name)) {
      setSelectedConditions(
        selectedConditions.filter((c) => c.name !== condition.name)
      );
    } else {
      setSelectedConditions([...selectedConditions, condition]);
    }
  };

  const onClearAll = () => setSelectedConditions([]);

  const onContinue = () => {
    const formattedConditions = selectedConditions?.length
      ? selectedConditions.map((cond) => ({
          choice: cond.name,
          id: cond.id,
        }))
      : [{ choice: 'None', id: null }];
    const englishConditions = isSpanish ? null : formattedConditions;
    const spanishConditions = isSpanish ? formattedConditions : null;

    onSubmit({
      choice_value: englishConditions,
      spanish_choice_value: spanishConditions,
      questionData,
      code: questionData?.code,
    });
  };

  return (
    <>
      <QuestionnaireContainer
        questionData={questionData}
        isLoading={isLoading}
        onContinue={onContinue}
        questionNumber={11}
      >
        <button
          onClick={() => setOpenSearch(true)}
          className='flex items-center w-2/3 gap-2 px-4 text-sm text-gray-500 bg-gray-100 rounded-md h-9'
        >
          <Search className='size-4' />
          <span className='pt-0.5 font-medium'>Search here...</span>
        </button>
        <div className='mt-4'>
          <h1 className='mb-1 font-semibold'>Most Common</h1>
          <div className='flex flex-wrap gap-2'>
            {localeCommonConditions.map((condition) => {
              const isSelected = selectedConditions
                .map((cond) => cond.name)
                .includes(condition.name);
              const { label, comment } = extractLabelAndComment(condition.name);
              return (
                <motion.div
                  key={condition.name}
                  className={cn(
                    'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150',
                    isSelected && 'text-white bg-primary'
                  )}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => onHandleSelection(condition)}
                >
                  <p className='leading-5'>
                    {label} {comment}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div className='mt-4'>
          <div className='flex items-center justify-between'>
            <h1 className='font-semibold'>Selected</h1>
            <Button
              variant='link'
              className='text-sm underline transition-all duration-300 hover:no-underline text-rose-500 h-9'
              disabled={!selectedConditions.length}
              onClick={onClearAll}
            >
              Clear all
            </Button>
          </div>
          <div className='flex flex-wrap gap-2'>
            {selectedConditions?.length ? (
              selectedConditions.map((condition) => {
                const { label, comment } = extractLabelAndComment(
                  condition.name
                );
                return (
                  <motion.div
                    key={condition.name}
                    className={cn(
                      'border border-gray-300 text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-between cursor-pointer transition-all duration-150 gap-1.5'
                    )}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    onClick={() => onHandleSelection(condition)}
                  >
                    <p className='leading-5'>
                      {label} {comment}
                    </p>
                    <X className='w-4 h-4' />
                  </motion.div>
                );
              })
            ) : (
              <div>
                <div
                  className={cn(
                    'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150'
                  )}
                >
                  <p className='leading-5'>None</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </QuestionnaireContainer>

      <MedicalConditionSearch
        open={openSearch}
        onClose={() => setOpenSearch(false)}
        setSelected={setSearchValues}
        searchOptions={
          searchOptions || { eng_choices: [], spanish_choices: [] }
        }
      />
    </>
  );
}
